import { Button, Typography } from "@mui/material"
import { SingleServiceCenterDto } from "api"
import Image from "next/image"
import styled from "styled-components"
import { StyledCol, StyledRow, StyledSvgIcon } from "theme/shared"
import CardRowText from "../../CardRowText"
import SocialIconButton from "./SocialIconButton"
const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 100px;
  }
`
const StyledCarHeadline = styled(Typography).attrs({ variant: "h1" })`
  text-align: start;
  font-size: 24px;
  font-weight: 400;
`
const StyledHeadline = styled(Typography).attrs({ variant: "h3" })`
  margin-top: 0;
  display: inline-block;
`
const StyledImageWrap = styled.div`
  width: 140px;
  height: 110px;
  border: 0.53125px solid ${(p) => p.theme.colors.darkGrey};
  border-radius: 8.5px;
  overflow: hidden;
  margin-bottom: 8px;
`
type CenterProfileProps = {
  centerData: SingleServiceCenterDto
  openTreatmentFunc: () => void
  openContentUsFunc: () => void
}

const CenterProfile = ({
  centerData,
  openTreatmentFunc,
  openContentUsFunc,
}: CenterProfileProps) => {
  // All possible days in order
  const allDays = ["א", "ב", "ג", "ד", "ה", "ו", "שבת"]

  // Parse working schedule - handle both object and string array formats
  const parseWorkingSchedule = (scheduleData: string[] | any) => {
    if (!scheduleData) return {}

    // If it's already an object, return it
    if (typeof scheduleData === "object" && !Array.isArray(scheduleData)) {
      return scheduleData
    }

    // If it's an array of strings, parse each string
    if (Array.isArray(scheduleData)) {
      const result: { [key: string]: string[] } = {}

      scheduleData.forEach((item) => {
        // Expected format: "08:00-17:00: ['א', 'ב', 'ג', 'ד', 'ה']" or similar
        const regex = /(\d{2}:\d{2}-\d{2}:\d{2}):[^[]*\[([^\]]+)\]/
        const match = regex.exec(item)

        if (match) {
          const [, timeRange, daysStr] = match
          const days = daysStr
            .split(",")
            .map((d) => d.replace(/['"\s]/g, ""))
            .filter(Boolean)

          result[timeRange] = days
        }
      })

      return result
    }

    return {}
  }

  // Get schedule entries with closed days
  const getScheduleEntries = () => {
    const parsedSchedule = parseWorkingSchedule(centerData.workingSchedule)
    const workingDays = new Set<string>()

    // Collect all working days
    Object.values(parsedSchedule).forEach((days: string[]) => {
      days.forEach((day) => workingDays.add(day))
    })

    const entries: Array<{ display: string; isClosed: boolean }> = []

    // Add working schedule entries
    Object.entries(parsedSchedule).forEach(([time, days]) => {
      entries.push({
        display: `${(days as string[]).join(", ")} : ${time}`,
        isClosed: false,
      })
    }) // Add closed days
    const closedDays = allDays.filter((day) => !workingDays.has(day))
    if (closedDays.length > 0) {
      entries.push({
        display: `${closedDays.join(", ")} : סגור`,
        isClosed: true,
      })
    }

    return entries
  }

  return (
    <StyledWrapper>
      {/* col1: */}
      <StyledCol $notCenter sx={{ maxWidth: "100%" }}>
        {centerData?.logo?.imageUrl && (
          <StyledImageWrap>
            <Image
              src={centerData?.logo.imageUrl}
              alt={"דלק מוטורס"}
              width={140}
              height={110}
              priority
              style={{
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </StyledImageWrap>
        )}
        <StyledCarHeadline>{centerData?.name}</StyledCarHeadline>
        <StyledRow $gap="15px" $fullWidth $mt="20px">
          {!centerData?.isMainGarage && (
            <Button
              onClick={() => {
                openTreatmentFunc()
              }}
            >
              קביעת ביקור
            </Button>
          )}
          <Button
            onClick={() => {
              openContentUsFunc()
            }}
            variant="outlined"
          >
            יצירת קשר
          </Button>
        </StyledRow>
      </StyledCol>
      {/* col2: */}
      <StyledCol $gap="15px" $fullWidth $notCenter>
        <CardRowText
          data={centerData.address}
          iconName="mazdaLocationIcon"
          imageAlt={"מיקום"}
        />
        <CardRowText
          data={centerData.whatsapp}
          link={centerData.whatsappLink}
          iconName="whatsappLogo"
          imageAlt="ווטסאפ"
        />
        <CardRowText
          data={centerData.phone}
          link={`tel:${centerData.phone}`}
          iconName="phoneCall"
          imageAlt="טלפון"
        />
        <CardRowText data={centerData.fax} iconName="fax" imageAlt="פקס" />
        <CardRowText
          data={centerData.email}
          link={`mailto:${centerData.email}`}
          iconName="mailIcon"
          imageAlt="מייל"
        />
      </StyledCol>
      {/* col3 */}
      {centerData?.workingSchedule && (
        <StyledCol $notCenter $fullWidth>
          <StyledRow $gap="12px">
            <StyledSvgIcon
              src={`/icons/clock.svg`}
              alt="שעות פעילות"
              $w={20}
              $h={20}
            />
            <StyledHeadline>שעות פעילות</StyledHeadline>
          </StyledRow>
          {getScheduleEntries().map((entry, i) => (
            <StyledRow key={i} $mt="5px">
              <CardRowText data={entry.display} />
            </StyledRow>
          ))}
          {(centerData.siteName ||
            centerData.twitter ||
            centerData.instegram ||
            centerData.facebook) && (
            <StyledRow $mt="17px" $gap="10px">
              <StyledHeadline>בקרו אותנו</StyledHeadline>
              <SocialIconButton
                link={centerData.siteName}
                iconName="social/website"
                alt={"website icon"}
              />
              <SocialIconButton
                link={centerData.twitter}
                iconName="social/twitter"
                alt={"twitter icon"}
              />
              <SocialIconButton
                link={centerData.instegram}
                iconName="social/insta"
                alt={"instagram icon"}
              />
              <SocialIconButton
                link={centerData.facebook}
                iconName="social/facebook"
                alt={"facebook icon"}
              />
            </StyledRow>
          )}
        </StyledCol>
      )}
    </StyledWrapper>
  )
}

export default CenterProfile
